/**
 * Generated by orval v8.20.0.
 * Do not edit manually.
 * Hub API v2.0.0.
 */

import type { HttpContext, HttpEvent, HttpParams } from '@angular/common/http';
import { HttpResponse as AngularHttpResponse, HttpClient, HttpHeaders } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import type {
  AgencyStatsBreakdown200,
  AgencyStatsBreakdownParams,
  AgencyStatsTimeline200,
  AgencyStatsTimelineParams,
} from '../../models';

interface HttpClientOptions {
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly context?: HttpContext;
  readonly params?:
    | HttpParams
    | Record<string, string | number | boolean | Array<string | number | boolean>>;
  readonly reportProgress?: boolean;
  readonly withCredentials?: boolean;
  readonly credentials?: RequestCredentials;
  readonly keepalive?: boolean;
  readonly priority?: RequestPriority;
  readonly cache?: RequestCache;
  readonly mode?: RequestMode;
  readonly redirect?: RequestRedirect;
  readonly referrer?: string;
  readonly integrity?: string;
  readonly referrerPolicy?: ReferrerPolicy;
  readonly transferCache?: { includeHeaders?: string[] } | boolean;
  readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
  readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
  readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
  readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
  readonly observe?: 'body' | 'events' | 'response';
};

type AngularHttpParamValue = string | number | boolean | Array<string | number | boolean>;
type AngularHttpParamValueWithNullable = AngularHttpParamValue | null;

function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys?: ReadonlySet<string>,
  preserveRequiredNullables?: false,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValue>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: true,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValueWithNullable>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: boolean | undefined,
  passthroughKeys: ReadonlySet<string>
): Record<string, unknown>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> = new Set(),
  preserveRequiredNullables = false,
  passthroughKeys: ReadonlySet<string> = new Set()
): Record<string, unknown> {
  const filteredParams: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (passthroughKeys.has(key)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
      continue;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter(
        item =>
          item != null &&
          (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
      ) as Array<string | number | boolean>;
      if (filtered.length) {
        filteredParams[key] = filtered;
      }
    } else if (preserveRequiredNullables && value === null && requiredNullableKeys.has(key)) {
      filteredParams[key] = null;
    } else if (
      value != null &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    ) {
      filteredParams[key] = value;
    }
  }
  return filteredParams;
}

@Injectable({ providedIn: 'root' })
export class StatsService {
  private readonly http = inject(HttpClient);
  /**
   * Returns the order totals per entity of the chosen dimension over the requested window, paginated by row. The `provider` grouping pairs each provider with the application its orders came through, one row per pair; the `agent` grouping leaves out superusers and the orders no existing agent placed, so it has no unattributed row, where the other groupings report those under a null `entity`. The consumer owner counts the whole consumer, or one of its agencies named in `agency_id`; every other manager counts their own agency and may not name one. Defaults to the last 60 days, `date_end` included; the span is capped at 366 days. Managers of the agency only, sub-agency managers included.<br><br>Replaces `GET /v1.5/agent/stats/summary` of the legacy agent API, the grouped view, which the Hub calls the breakdown (the legacy `breakdown` is now `GET /agency/stats/timeline`): `listing` is `group_by` and is required; the per-dimension `provider`, `account`, `agency` and `application` objects are one `entity`, whose `name` is the provider's code, the agent's email or the agency's or application's name; the flat `orders_*` totals are under `counts`; `disrupted` is gone and `refunded` is counted in its place; the rows are paginated; and `date_end` is now included in the window.
   * @summary Agency breakdown stats
   */
  agencyStatsBreakdown<TData = AgencyStatsBreakdown200>(
    params: AgencyStatsBreakdownParams,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyStatsBreakdown<TData = AgencyStatsBreakdown200>(
    params: AgencyStatsBreakdownParams,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyStatsBreakdown<TData = AgencyStatsBreakdown200>(
    params: AgencyStatsBreakdownParams,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyStatsBreakdown<TData = AgencyStatsBreakdown200>(
    params: AgencyStatsBreakdownParams,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({ ...params, ...options?.params }, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/breakdown`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/breakdown`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/breakdown`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Returns the order counts bucketed by interval and metric over the requested window, for the dashboard and reports chart. The consumer owner counts the whole consumer, or one of its agencies named in `agency_id`; every other manager counts their own agency and may not name one. Defaults to the last 60 days, `date_end` included; the range may span at most 366 buckets of the chosen interval. Managers of the agency only, sub-agency managers included.<br><br>Replaces `GET /v1.5/agent/stats/breakdown` of the legacy agent API, the bucketed view, which the Hub calls the timeline (the legacy `summary` is now `GET /agency/stats/breakdown`): `range` is `interval`; each row's `year`, `week` and `created_at` are one `bucket` date, the period's first day (Monday for a week); `type` is the metric (`created`, `issued`, `cancelled`, `refunded`, `voided`, `expired`, `completed`) rather than the event type name; `disrupted` is gone and `refunded` is counted in its place; and `date_end` is now included in the window.
   * @summary Agency timeline stats
   */
  agencyStatsTimeline<TData = AgencyStatsTimeline200>(
    params: AgencyStatsTimelineParams,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyStatsTimeline<TData = AgencyStatsTimeline200>(
    params: AgencyStatsTimelineParams,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyStatsTimeline<TData = AgencyStatsTimeline200>(
    params: AgencyStatsTimelineParams,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyStatsTimeline<TData = AgencyStatsTimeline200>(
    params: AgencyStatsTimelineParams,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({ ...params, ...options?.params }, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/timeline`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/timeline`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/stats/timeline`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
}
